<template>
  <div class="dass-timeline-wrapper">
    <div class="dass-horizontal-timeline-container">
      <div class="dass-horizontal-timeline">
        <div 
          v-for="(item, index) in timelineItems" 
          :key="index" 
          class="dass-timeline-item"
          :class="getItemStatus(item, index)"
        >
          <div class="dass-timeline-marker-container">
            <div class="dass-timeline-dot">
              <span v-if="isPastOrCurrent(index)" class="dass-check-icon">✓</span>
              <span v-else class="dot"></span>
            </div>
            
            <div 
              class="dass-timeline-connector" 
              v-if="index < timelineItems.length - 1"
              :class="{'completed': isPastOrCurrent(index)}"
            ></div>
          </div>

          <div class="dass-timeline-content-container">
            <div 
              class="dass-timeline-label"
              @click="toggleDetails(index)"
            >
              {{ item.label }}
              <div v-if="item?.date" class="dass-timeline-date">{{ $filters.toDateBRL(item?.date) }} - {{ $filters.toHourBRL(item?.date, false, 'America/Sao_Paulo') }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="dass-timeline-cards-container">
      <Transition name="slide-fade">
        <div 
          v-if="activeCard !== null"
          class="timeline-card"
        >
          <div class="card">
            <div class="dass-card-header">
              <div>
                <h3>{{ $t('protocolo.status') }}:</h3>
                <b>{{ activeCardItem?.SIT_NOME || activeCardItem?.label }}</b>
              </div>
              <div v-if="activeCardItem?.date || activeCardItem.PHS_DATA">
                <h3>{{ $t('dass.dtAtendimento') }}:</h3>
                <b class="date">
                  {{ $filters.toDateBRL(activeCardItem.date || activeCardItem.PHS_DATA) }} - 
                  {{ $filters.toHourBRL(activeCardItem.date, false, 'America/Sao_Paulo' || activeCardItem.PHS_DATA, false, 'America/Sao_Paulo') }}
                </b>
              </div>
              <div v-if="activeCardItem.PHS_ANEXO">
                <h3>{{ $t('dass.attachment') }}:</h3>
                <q-btn
                  flat
                  round
                  dense
                  color="red-9"
                  icon="picture_as_pdf"
                  @click="showModalPreviewHtml = true"
                >
                  <q-tooltip>{{ $t('dass.viewPdf') }}</q-tooltip>
                </q-btn>                
              </div>
              <div v-if="activeCardItem?.MSG_DESC">
                <h3>{{ $t('dass.observacoes') }}:</h3>
                <b>{{ activeCardItem?.PHS_OBS }} </b>
              </div> 
            </div>
            <div v-if="activeCardItem?.MSG_DESC" class="card-body">
              <div class="dass-obs-msg">
                <span v-html="activeCardItem?.MSG_DESC"></span>
              </div> 
            </div>
            <div class="card-footer">
              <b @click="closeCard" class="close">{{ $t('dass.close') }}</b>
            </div>
          </div>
        </div>
      </Transition>
      <ModalPreviewHtml
        v-if="showModalPreviewHtml"
        :isModalOpen="showModalPreviewHtml"
        :titleModal="$t('dass.viewPdf')"
        :titleHtml="$t('dass.reibursementConfirmation')"
        :emailHtml="activeCardItem.PHS_ANEXO"
        :downloadAction="true"
        @closeAction="showModalPreviewHtml = false"
      />
    </div>
  </div>
</template>
<script>
import ModalPreviewHtml from '../../molecules/ModalPreviewHtml.vue'
export default {
  name: 'HorizontalTimeline',
  components: { ModalPreviewHtml },
  props: {
    pastAndCurrentItems: {
      type: Array,
      required: true,
      validator: items => items.every(item => {
        const hasLabel = typeof item.label !== 'undefined' || typeof item.SIT_NOME !== 'undefined'
        const needsDate = items.indexOf(item) < items.length - 1
        return hasLabel && (!needsDate || (item.date && !isNaN(new Date(item.date).getTime())) || (item.PHS_DATA && !isNaN(new Date(item.PHS_DATA).getTime())))
      })
    },
    futureItems: {
      type: Array,
      default: () => [],
      validator: items => items.every(item => typeof item.label !== 'undefined')
    }
  },
  data() {
    return {
      activeCard: null, 
      showModalPreviewHtml: false
    }
  },
  computed: {
    timelineItems() {
      const sortedPast = [...this.pastAndCurrentItems.slice(0, -1)].sort((a, b) => 
        new Date(a.date || a.PHS_DATA) - new Date(b.date || b.PHS_DATA)
      )
      const currentItem = this.pastAndCurrentItems[this.pastAndCurrentItems.length - 1]
      return [...sortedPast, currentItem, ...this.futureItems]
    },
    currentItemIndex() {
      return this.pastAndCurrentItems.length - 1
    },
    activeCardItem() {
      return (this.activeCard !== null && this.timelineItems[this.activeCard]) 
          ? this.timelineItems[this.activeCard] 
          : {};
    }
  },
  methods: {
    isPastOrCurrent(index) {
      return index <= this.currentItemIndex
    },
    getItemStatus(item, index) {
      if (index === this.currentItemIndex) return 'current'
      return index < this.currentItemIndex ? 'completed' : 'future'
    },    
    toggleDetails(index) {
      this.activeCard = this.activeCard === index ? null : index
      if (this.activeCard !== null) {
        this.$emit('item-click', { 
          index: this.activeCard, 
          item: this.activeCardItem 
        })
      }
    },
    closeCard() {
      this.activeCard = null
    }
  },
  mounted() {    
    this.activeCard = this.currentItemIndex;
  },
}
</script>

<style scoped>
.dass-horizontal-timeline-container {
  width: 100%;
  overflow-x: auto;
  padding: 20px 0;
  scrollbar-width: thin;
}
.dass-horizontal-timeline-container::-webkit-scrollbar {
  height: 11px;  
}

.dass-horizontal-timeline-container::-webkit-scrollbar-thumb {
  background-color: #ccc;
}
.dass-timeline-wrapper {
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.dass-horizontal-timeline {
  display: inline-flex;
  min-width: 100%;
  align-items: flex-start;
  justify-content: center;
  position: relative;
  padding-bottom: 20px;
  margin-left: 2rem;
  margin-right: 2rem;
}

.dass-timeline-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  min-width: 165px;
  max-width: 190px;  
  flex-shrink: 0;  
  height: min-content;
}

.dass-timeline-marker-container {
  display: flex;
  align-items: center;
  position: relative;
  width: 100%;
  justify-content: center;
}
.dass-timeline-dot {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 2;
}

.dass-timeline-dot .dot {
  display: block;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: #ccc;
}

.dass-check-icon {
  color: white;
  font-weight: bold;
}

.completed .dass-timeline-dot {
  background-color: #4CAF50;
}

.current .dass-timeline-dot {
  background-color: #2196F3;
}

.future .dass-timeline-dot {
  background-color: transparent;
  border: 2px solid #ccc;
}

.dass-timeline-connector {
  height: 2px;
  background-color: #ccc;
  position: absolute;
  left: 50%;
  right:-50%;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;
}

.completed .dass-timeline-connector {
  background-color: #4CAF50;
}

.dass-timeline-content-container {
  margin-top: 8px;
  text-align: center;
  width: 100%;
  position: relative;
}

.dass-timeline-label {
  font-size: 1rem;
  color: #333;
  white-space: normal;   
  word-wrap: break-word;
  cursor: pointer;
  line-height: 1.2;
  transition: all 0.2s ease;
  padding: 4px 8px;
  border-radius: 4px;
}

.dass-timeline-label:hover {
  background-color: #f5f5f5;
  transform: translateY(-2px);
}

.dass-timeline-date {
  font-size: 1rem;
  color: #666;
  margin-top: 2px;
  white-space: nowrap;
}

.completed .dass-timeline-label,
.completed .dass-timeline-date {
  color: #4CAF50;
  font-weight: bold;
}

.current .dass-timeline-label,
.current .dass-timeline-date {
  color: #2196F3;
  font-weight: bold;
}

.timeline-description {
  font-size: 11px;
  color: #555;
  margin-top: 6px;
  min-height: 20px;
  word-break: break-word;
  white-space: normal;
}

.dass-timeline-cards-container {
  width: 100%;
}
.timeline-card {
  width: 100%;  
  margin: 0 auto 20px;
}
.dass-obs-msg {
    width: 100%;   
    max-height: 250px; 
    overflow-y: scroll;
    text-align: center;    
    > span {
      font-size: 1.2rem;
    }
  }
.card {
  background-color: #fff;
  border: 1px solid #ddd;
  border-radius: var(--theme-border-radius);      
  margin-bottom: var(--space__2x);
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
}

.dass-card-header {
  background-color: var(--color-neutral__07);
  padding: var(--space__1-5x);      
  display: flex;      
  justify-content: space-between;
  width: 100%;
  align-items: flex-start;
  flex-wrap: wrap; 
  border-top-left-radius: var(--theme-border-radius);
  border-top-right-radius: var(--theme-border-radius);
  > div {
    flex: 1 1 50%;
    min-width: 250px;
    text-align: center;

    > h3 {
      color: var(--theme-success) !important;
      margin-bottom: 0;
      padding-bottom: 0;
    }
    b {
      font-size: 1.2rem;  
    }
    
  }
}

.card-body {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: var(--space__1-5x);
  width: 100%;
  
}

.card-footer {
  background-color: var(--color-neutral__07);
  padding: var(--space);
  border-bottom-left-radius: var(--theme-border-radius);
  border-bottom-right-radius: var(--theme-border-radius);
  b {       
    font-size: 1.2rem;  
    color: var(--theme-success);     
    &:hover {
      cursor: pointer;
      text-decoration: underline;
    }
  }
}
  
.close {
  cursor: pointer;
}

.close:hover {
  text-decoration: underline;
}

/* Transition effects */
.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.2s cubic-bezier(1, 0.5, 0.8, 1);
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateY(-10px) translateX(-50%);
  opacity: 0;
}
</style>